import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Receipt, AlertCircle } from 'lucide-react';
import api from '../../../services/api';

// Auszahlungs-Transparenz: zeigt für einen gesettelten Markt, welcher
// User wieviel VC bekommen hat. Quelle: GET /admin/markets/{id}/payouts.
const SettlementPayoutsCard = ({ markets }) => {
  const [marketId, setMarketId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const settledMarkets = (markets || []).filter((m) => m.status === 'settled');

  const load = async (id) => {
    setMarketId(id);
    setData(null);
    setError('');
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.admin.getMarketPayouts(id);
      setData(res.data);
    } catch {
      setError('Auszahlungen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  const statusLabel = (s) => (
    s === 'won' ? 'Gewonnen' :
    s === 'refunded' ? 'Erstattet' :
    s === 'lost' ? 'Verloren' :
    s === 'placed' ? 'Offen' : s
  );
  const statusClass = (s) => (
    s === 'won' ? 'text-emerald-600 dark:text-emerald-400' :
    s === 'refunded' ? 'text-blue-600 dark:text-blue-400' :
    s === 'lost' ? 'text-gray-500 dark:text-gray-400' :
    'text-gray-700 dark:text-gray-300'
  );
  const fmt = (n) => (Number(n) || 0).toLocaleString('de-DE');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          Auszahlungen ansehen
        </CardTitle>
        <CardDescription>
          Wer hat beim Settlen eines Marktes wieviel VC erhalten?
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <Label htmlFor="payout_market_select">Gesettelten Markt wählen</Label>
          <select
            id="payout_market_select"
            className="w-full p-2 border rounded-md dark:bg-slate-900/80"
            value={marketId}
            onChange={(e) => load(e.target.value)}
          >
            <option value="">Markt wählen…</option>
            {settledMarkets.map((m) => (
              <option key={m.id} value={m.id}>
                {m.title}{m.winning_outcome_label ? ` — Gewinner: ${m.winning_outcome_label}` : ''}
              </option>
            ))}
          </select>
          {settledMarkets.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Noch keine gesettelten Märkte vorhanden.
            </p>
          )}
        </div>

        {loading && (
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Lade Auszahlungen…</p>
        )}
        {error && (
          <Alert className="mt-4" variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {data && !loading && (
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span><span className="text-gray-500 dark:text-gray-400">Gewinner:</span> <strong>{data.winners_count}</strong></span>
              <span><span className="text-gray-500 dark:text-gray-400">Wetten gesamt:</span> <strong>{data.bets_count}</strong></span>
              <span><span className="text-gray-500 dark:text-gray-400">Einsätze gesamt:</span> <strong>{fmt(data.total_stake)} VC</strong></span>
              <span><span className="text-gray-500 dark:text-gray-400">Auszahlung gesamt:</span> <strong className="text-emerald-600 dark:text-emerald-400">{fmt(data.total_payout)} VC</strong></span>
            </div>

            {data.payouts.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Keine Wetten auf diesem Markt.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-slate-700">
                      <th className="p-2">User</th>
                      <th className="p-2 text-right">Einsatz</th>
                      <th className="p-2 text-right">Auszahlung</th>
                      <th className="p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.payouts.map((p) => (
                      <tr key={p.bet_id} className="border-b border-gray-100 dark:border-slate-800">
                        <td className="p-2">{p.handle}</td>
                        <td className="p-2 text-right tabular-nums">{fmt(p.stake)} VC</td>
                        <td className="p-2 text-right tabular-nums">
                          {p.payout > 0 ? `${fmt(p.payout)} VC` : '—'}
                        </td>
                        <td className={`p-2 ${statusClass(p.status)}`}>{statusLabel(p.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SettlementPayoutsCard;
